import { z } from 'zod';
import { EMBEDDED_LIMITS, REQUEST_NUMBER_PATTERN, REQUEST_STATUSES, REQUEST_TYPES, } from '../../constants/index.js';
import { localeSchema, mediaRefSchema, objectIdSchema, phoneSchema, priceSchema, } from '../common.js';
import { auditableSchema } from './base.js';
export const requestTypeSchema = z.enum(REQUEST_TYPES);
export const requestStatusSchema = z.enum(REQUEST_STATUSES);
/** Barcha ariza turlarida bir xil kontakt bloki (TZ 3.13). */
export const requestContactSchema = z.object({
    name: z.string().trim().min(2, 'Ism kamida 2 belgi').max(200),
    phone: phoneSchema,
    email: z.string().trim().toLowerCase().email().max(200).nullable().default(null),
    company: z.string().trim().max(300).nullable().default(null),
    preferredContact: z.enum(['phone', 'telegram', 'email']).nullable().default(null),
});
// ─── Payload sxemalari ───────────────────────────────────────────────────────
// Har bir tur uchun alohida. Yuborilgan paytdagi nom va narx SNAPSHOT sifatida
// saqlanadi — mahsulot keyin o'zgarsa ham ariza o'zgarmaydi (TZ 5.2).
export const productPayloadSchema = z.object({
    kind: z.literal('product'),
    productId: objectIdSchema,
    productTitle: z.string().max(300),
    variantId: z.string().max(40),
    variantTitle: z.string().max(300),
    price: priceSchema,
    quantity: z.number().int().min(1).max(999).default(1),
});
export const cartItemSchema = z.object({
    productId: objectIdSchema,
    productTitle: z.string().max(300),
    variantId: z.string().max(40),
    variantTitle: z.string().max(300),
    price: priceSchema,
    quantity: z.number().int().min(1).max(999).default(1),
});
export const cartPayloadSchema = z.object({
    kind: z.literal('cart'),
    items: z.array(cartItemSchema).min(1).max(50),
    /** Narxi ma'lum pozitsiyalar yig'indisi. Narxsizlari qo'shilmaydi. */
    totalAmount: z.number().int().nonnegative().nullable().default(null),
    deliveryRegion: z.string().trim().max(200).nullable().default(null),
});
export const droneShowPayloadSchema = z.object({
    kind: z.literal('drone_show'),
    packageId: objectIdSchema.nullable().default(null),
    eventType: z.string().trim().max(200),
    eventDate: z.coerce.date().nullable().default(null),
    eventCity: z.string().trim().max(200),
    venue: z.string().trim().max(300).nullable().default(null),
    audienceSize: z.number().int().min(0).max(1_000_000).nullable().default(null),
    droneCount: z.number().int().min(0).max(10_000).nullable().default(null),
    needPyro: z.boolean().default(false),
    durationMinutes: z.number().int().min(0).max(120).nullable().default(null),
    budgetRange: z.string().trim().max(200).nullable().default(null),
    referenceFiles: z.array(mediaRefSchema).max(5).default([]),
});
export const repairPayloadSchema = z.object({
    kind: z.literal('repair'),
    droneModel: z.string().trim().min(1).max(200),
    serialNumber: z.string().trim().max(100).nullable().default(null),
    problemDescription: z.string().trim().min(5).max(4000),
    underWarranty: z.boolean().nullable().default(null),
    photos: z.array(mediaRefSchema).max(5).default([]),
});
export const coursePayloadSchema = z.object({
    kind: z.literal('course'),
    courseId: objectIdSchema,
    courseTitle: z.string().max(300),
    experienceLevel: z.enum(['none', 'beginner', 'experienced']).default('none'),
    participantsCount: z.number().int().min(1).max(100).default(1),
    preferredStartDate: z.coerce.date().nullable().default(null),
});
export const servicePayloadSchema = z.object({
    kind: z.literal('service'),
    serviceId: objectIdSchema,
    serviceTitle: z.string().max(300),
    objectType: z.string().trim().max(200).nullable().default(null),
    areaHectares: z.number().nonnegative().max(1_000_000).nullable().default(null),
    region: z.string().trim().max(200).nullable().default(null),
    deadline: z.coerce.date().nullable().default(null),
    files: z.array(mediaRefSchema).max(5).default([]),
});
export const generalPayloadSchema = z.object({
    kind: z.literal('general'),
    subject: z.string().trim().max(300).nullable().default(null),
});
export const requestPayloadSchema = z.discriminatedUnion('kind', [
    productPayloadSchema,
    cartPayloadSchema,
    droneShowPayloadSchema,
    repairPayloadSchema,
    coursePayloadSchema,
    servicePayloadSchema,
    generalPayloadSchema,
]);
/** `type` va `payload.kind` doim mos bo'lishi kerak. */
export function payloadMatchesType(type, payload) {
    if (payload === null)
        return type === 'general';
    return payload.kind === type;
}
// ─── Ariza hujjati ───────────────────────────────────────────────────────────
export const requestNoteSchema = z.object({
    userId: objectIdSchema,
    text: z.string().trim().min(1).max(4000),
    createdAt: z.coerce.date(),
});
export const requestSchema = auditableSchema.omit({ deletedAt: true }).extend({
    /** GEO-2026-00042 */
    requestNumber: z.string().regex(REQUEST_NUMBER_PATTERN),
    type: requestTypeSchema,
    contact: requestContactSchema,
    comment: z.string().trim().max(4000).nullable().default(null),
    payload: requestPayloadSchema.nullable().default(null),
    // Texnik ma'lumot — avtomatik to'ldiriladi
    sourceUrl: z.string().max(500).default(''),
    locale: localeSchema.default('uz'),
    ip: z.string().max(64).default(''),
    userAgent: z.string().max(500).default(''),
    recaptchaScore: z.number().min(0).max(1).nullable().default(null),
    utm: z
        .object({
        source: z.string().max(200).nullable().default(null),
        medium: z.string().max(200).nullable().default(null),
        campaign: z.string().max(200).nullable().default(null),
        content: z.string().max(200).nullable().default(null),
        term: z.string().max(200).nullable().default(null),
    })
        .nullable()
        .default(null),
    // Boshqaruv
    status: requestStatusSchema.default('new'),
    assignedTo: objectIdSchema.nullable().default(null),
    notes: z.array(requestNoteSchema).max(EMBEDDED_LIMITS.REQUEST_NOTES).default([]),
    estimatedAmount: z.number().int().nonnegative().nullable().default(null),
    // Telegram
    telegramMessageId: z.number().int().nullable().default(null),
    telegramSentAt: z.coerce.date().nullable().default(null),
    telegramError: z.string().max(1000).nullable().default(null),
});
// ─── Kirish sxemasi (ommaviy forma) ──────────────────────────────────────────
export const createRequestSchema = z
    .object({
    type: requestTypeSchema,
    contact: requestContactSchema,
    comment: z.string().trim().max(4000).nullable().default(null),
    payload: requestPayloadSchema.nullable().default(null),
    sourceUrl: z.string().max(500).default(''),
    locale: localeSchema.default('uz'),
    recaptchaToken: z.string().max(4000).optional(),
    /** Ko'rinmas maydon — robotlar to'ldiradi (TZ 5.6) */
    website: z.string().max(200).optional(),
    /** Forma ochilgan vaqt (ms) — 3 soniyadan tez yuborilsa rad etiladi */
    formOpenedAt: z.number().int().nonnegative().optional(),
    consent: z.literal(true, {
        error: 'Shaxsiy ma‘lumotlarni qayta ishlashga rozilik kerak',
    }),
})
    .refine((v) => payloadMatchesType(v.type, v.payload), {
    message: 'Ariza turi va tarkibi mos emas',
    path: ['payload'],
});
export const listRequestsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).max(100).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    type: requestTypeSchema.optional(),
    status: requestStatusSchema.optional(),
    assignedTo: objectIdSchema.optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    search: z.string().trim().max(200).optional(),
});
//# sourceMappingURL=request.js.map